import type { ReviewCard, ReviewState } from "./ggb-review.js";
import { isCalendarDate } from "../../../../domain/round/round-date.js";
import { validateTotalsReview } from "./ggb-totals-review.js";

/** Interpret the explicitly supplied course-local date and UTC offset. */
export function importPlayedAt(
  review: Pick<ReviewState, "playedAt" | "utcOffset" | "datePrecision">,
): string | null {
  if (review.datePrecision === "unknown") return null;
  if (review.datePrecision === "date")
    return isCalendarDate(review.playedAt) ? review.playedAt : null;
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(review.playedAt) ||
    !/^[+-](?:0\d|1[0-3]):(?:00|15|30|45)$|^[+-]14:00$/.test(review.utcOffset)
  )
    return null;
  const plain = new Date(`${review.playedAt}:00.000Z`);
  if (
    !Number.isFinite(plain.getTime()) ||
    plain.toISOString().slice(0, 16) !== review.playedAt
  )
    return null;
  const instant = new Date(`${review.playedAt}:00${review.utcOffset}`);
  return Number.isFinite(instant.getTime()) ? instant.toISOString() : null;
}

export function validateImportMetadata(
  review: ReviewState,
  assigned: ReviewCard[],
): string | null {
  if (!review.course.trim()) return "Enter the course shown on the scorecards.";
  const date = importPlayedAt(review);
  if (!date && review.datePrecision !== "unknown")
    return review.datePrecision === "date"
      ? "Enter the date actually played, or choose Date unknown."
      : "Enter the historical round start and its UTC offset.";
  if (
    date &&
    (review.datePrecision === "date"
      ? date > new Date().toISOString().slice(0, 10)
      : Date.parse(date) > Date.now())
  )
    return "The imported round must have been played in the past.";
  if (!["stroke-play", "stableford", "match-play"].includes(review.format))
    return "Choose the format actually played.";
  if (!["gross", "net"].includes(review.scoringBasis))
    return "Choose whether the result was gross or net.";
  if (review.format === "match-play" && assigned.length !== 2)
    return "Individual Match Play needs exactly two players.";
  if (review.detail === "totals-only") {
    const error = validateTotalsReview(review, assigned);
    return (
      error ??
      (review.confirmed
        ? null
        : "Confirm the actual totals, players and known round details before importing.")
    );
  }
  if (
    ![9, 18].includes(review.holes.length) ||
    ![1, 10].includes(review.holes[0] ?? 0) ||
    review.holes.some(
      (n, index) => n !== (review.holes[0] ?? 0) + index || n > 18,
    )
  )
    return "Import a complete front nine, back nine or 18-hole scorecard.";
  for (const card of assigned) {
    const problem = validateCardMetadata(card, review);
    if (problem) return problem;
  }
  return review.confirmed
    ? null
    : "Confirm the actual scores, players and round details before importing.";
}

function validateCardMetadata(
  card: ReviewCard,
  review: ReviewState,
): string | null {
  if (!card.teeName.trim())
    return `${card.name}: enter the tee actually played.`;
  if (card.assign === "__guest__" && (!card.named || !card.name.trim()))
    return "Assign unnamed scorecards to an existing player.";
  if (review.scoringBasis === "net" && !card.playingHandicap.trim())
    return `${card.name}: enter the Playing Handicap agreed for these ${review.holes.length} holes.`;
  if (
    card.playingHandicap.trim() &&
    (!/^-?\d+$/.test(card.playingHandicap) ||
      Number(card.playingHandicap) < -20 ||
      Number(card.playingHandicap) > 100)
  )
    return `${card.name}: Playing Handicap must be an integer from -20 to 100.`;
  const indexes: number[] = [];
  for (const hole of review.holes) {
    const par = Number(card.par[hole]);
    const si = Number(card.si[hole]);
    if (!Number.isInteger(par) || par < 2 || par > 7)
      return `${card.name}, hole ${hole}: enter the actual par (2–7).`;
    if (!Number.isInteger(si) || si < 1 || si > 18)
      return `${card.name}, hole ${hole}: enter its original stroke index (1–18).`;
    indexes.push(si);
  }
  return new Set(indexes).size === indexes.length
    ? null
    : `${card.name}: stroke indexes must be unique. Keep the original 1–18 indexes for nine holes.`;
}
